import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { fetchAllFilteredRows } from "@/components/shared/data-table/data-table-export";
import {
  type EstimateListFilters,
  getEstimate,
  getEstimates,
  getEstimateTotals,
} from "@/features/estimates/hooks/estimates-client";
import type { Estimate, EstimateFilters } from "@/features/estimates/types";
import { estimateKeys } from "@/features/estimates/hooks/query-keys";

interface UseEstimateOptions {
  enabled?: boolean;
}

export function useEstimates(filters: EstimateFilters) {
  return useQuery({
    queryKey: estimateKeys.list(filters as unknown as Record<string, unknown>),
    queryFn: () => getEstimates(filters),
  });
}

/**
 * Totals for the whole filtered set.
 *
 * Keyed on the filters that change the set, so paging or re-sorting reuses a
 * figure that cannot have moved.
 */
export function useEstimateTotals(filters: EstimateListFilters) {
  const totalsFilters: EstimateListFilters = {
    account_id: filters.account_id,
    date_from: filters.date_from,
    date_to: filters.date_to,
    detail_completed: filters.detail_completed,
    is_quick: filters.is_quick,
    is_sample: filters.is_sample,
    search: filters.search,
    status: filters.status,
  };

  return useQuery({
    queryKey: estimateKeys.totals(totalsFilters as unknown as Record<string, unknown>),
    queryFn: () => getEstimateTotals(totalsFilters),
  });
}

/** Every estimate the current filters select, for CSV export. */
export function fetchEstimatesForExport(filters: EstimateListFilters): Promise<Estimate[]> {
  return fetchAllFilteredRows<Estimate>(({ limit, offset }) =>
    getEstimates({ ...filters, limit, offset }),
  );
}

export function useEstimate(estimateId: string, options?: UseEstimateOptions) {
  return useQuery({
    enabled: options?.enabled ?? Boolean(estimateId),
    queryKey: estimateKeys.detail(estimateId || "missing"),
    queryFn: () => getEstimate(estimateId),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePrefetchEstimateDetail() {
  const queryClient = useQueryClient();
  return useCallback(
    (estimateId: string) =>
      queryClient.prefetchQuery({
        queryFn: () => getEstimate(estimateId),
        queryKey: estimateKeys.detail(estimateId),
        staleTime: 5 * 60 * 1000,
      }),
    [queryClient],
  );
}
